// Plan 20-06 Task 2 — Profile Inventory section (META-06 user-visible
// surface). Renders the cosmetics the player owns (server-authored
// profile.inventory ids from Plan 20-04's wire-shape extension).
//
// Design notes:
//   - Unlike AchievementsGrid (which renders ALL 30 cells with locked
//     styling), InventoryGrid only renders OWNED cosmetics. The "locked"
//     view of an inventory is conceptually wrong — owned vs not-owned is
//     about possession, not discovery (DEC-025 / DEC-031 design intent).
//   - Empty state surfaces a "keep leveling up" callout — the level cap
//     of 50 + the 15-entry schedule ensures every new player will reach
//     L2 quickly and see their first cosmetic.
//   - Unknown cosmetic ids are silently filtered out (forward-compat
//     against a future cosmetic_id removal still sitting in D1 history).

import type { JSX } from 'react'
import { useIntl } from 'react-intl'

import { COSMETICS_BY_ID, type CosmeticMeta } from '../data/cosmetic-meta'

export interface InventoryGridProps {
  /**
   * Server-authoritative list of owned cosmetic ids. Stable shape from
   * Plan 20-04 (always an array; empty for new users).
   */
  readonly ownedIds: ReadonlyArray<string>
}

export function InventoryGrid({ ownedIds }: InventoryGridProps): JSX.Element {
  const intl = useIntl()
  // Project ids → meta; drop unknown ids (T-20-39-style mitigation).
  const ownedMetas: ReadonlyArray<CosmeticMeta> = ownedIds
    .map((id) => COSMETICS_BY_ID.get(id))
    .filter((m): m is CosmeticMeta => m !== undefined)

  const empty = ownedMetas.length === 0

  return (
    <section
      aria-labelledby="inventory-section-heading"
      data-section="profile-inventory"
      data-testid="profile-inventory-section"
      data-empty={empty ? 'true' : 'false'}
      style={{
        width: '100%',
        maxWidth: '480px',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--color-card)',
        borderRadius: 'var(--radius-card)',
        boxShadow: 'var(--shadow-card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <h2
        id="inventory-section-heading"
        style={{
          margin: 0,
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-16)',
        }}
      >
        {intl.formatMessage({ id: 'profile.inventory_section.title' })}
      </h2>
      {empty ? (
        <p
          data-testid="profile-inventory-empty-state"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-13)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {intl.formatMessage({
            id: 'profile.inventory_section.empty_state',
          })}
        </p>
      ) : (
        <ul
          data-testid="profile-inventory-grid"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '8px',
          }}
        >
          {ownedMetas.map((meta) => (
            <li key={meta.id}>
              <CosmeticChip meta={meta} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

interface CosmeticChipProps {
  readonly meta: CosmeticMeta
}

function CosmeticChip({ meta }: CosmeticChipProps): JSX.Element {
  const intl = useIntl()
  const name = intl.formatMessage({ id: meta.i18n_key_name })
  const kindLabel = intl.formatMessage({
    id: `profile.inventory_section.kind_${meta.kind}`,
  })
  return (
    <div
      data-testid={`cosmetic-chip-${meta.id}`}
      data-kind={meta.kind}
      className={`cosmetic-chip kind-${meta.kind}`}
      role="img"
      aria-label={`${name} — ${kindLabel}`}
      style={{
        width: '100%',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        background: 'var(--color-card)',
        border: `3px solid ${meta.preview_color}`,
        borderRadius: 'var(--radius-tile)',
        boxShadow: 'var(--shadow-tile)',
      }}
    >
      <span
        data-testid={`cosmetic-swatch-${meta.id}`}
        aria-hidden="true"
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: meta.preview_color,
        }}
      />
      <span
        style={{
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-12)',
          lineHeight: 1.2,
          color: 'var(--color-text-primary)',
          textAlign: 'center',
          minHeight: '14px',
        }}
      >
        {name}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-9)',
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {kindLabel}
      </span>
    </div>
  )
}
